/**
 * Error thrown when a requested model cannot be found
 */
export class ModelNotFoundError extends Error {
  constructor(modelId: string) {
    super(`Model with id '${modelId}' not found`);
    this.name = 'ModelNotFoundError';
  }
}

/**
 * Core data structure for AI model information
 */
export enum ModelStatus {
  Running = 'running',
  Deprecated = 'deprecated',
}

/**
 * Type for data classification levels
 */
export type DataClassification =
  | 'internal'
  | 'confidential'
  | 'secret'
  | 'public';

/**
 * Type for nexus model restrictions
 */
export type RestrictionType =
  | 'DataLimitation'
  | 'InformationClassification'
  | 'PersonalData';

/**
 * Warning structure returned by the Nexus API
 * when a model has restrictions or limitations
 * that users should be aware of.
 */
export interface NexusModelAnnotation {
  title: string;
  content: string;
  id: string;
  level: 'info' | 'warning' | 'error';
}

/**
 * A single deployment entry returned by the Nexus API
 */
export interface NexusModelDeployment {
  modelDeploymentId: string;
  status: string;
  dataClassification: DataClassification;
  personalData: boolean;
  modelClusterProvider: string;
  modelClusterRegion: string;
  modelRegion: string;
  nexusClusterProvider: string;
  nexusClusterRegion: string;
  nexusEnvironment: string;
  nexusRegion: string;
}

/**
 * Reference links returned by the Nexus API
 */
export interface NexusModelReferenceLinks {
  apiPortal?: string;
  apiPortalManager?: string;
  modelWeights?: string;
  documentation?: string;
  termsOfUse?: string;
}

/**
 * Raw response shape from the Nexus /models and /models/{id} endpoints
 */
export interface NexusModelResponse {
  id: string;
  name: string;
  releaseDate: string;
  longDescription: string;
  shortDescription?: string;
  iconId: string;
  creator: string;
  licenseType?: string;
  selfHostable?: boolean;
  dataTypesInput: string[];
  dataTypesOutput: string[];
  informationLastUpdated: string;
  deployments: NexusModelDeployment[];
  referenceLinks: NexusModelReferenceLinks;
  annotations?: NexusModelAnnotation[];
}

/**
 * Model data used across the Model Garden pages
 */
export interface ModelData {
  id: string;
  name: string;
  releaseDate: string;
  longDescription: string;
  shortDescription?: string;
  licenseType?: string;
  iconId: string;
  creator: string;
  dataTypesInput: string[];
  dataTypesOutput: string[];
  informationLastUpdated: string;
  deployments: NexusModelDeployment[];
  referenceLinks: NexusModelReferenceLinks;
  annotations?: NexusModelAnnotation[];
  selfHostable?: boolean;
}

/**
 * Structure for models JSON file
 */
export interface ModelsData {
  resources: ModelData[];
}

/**
 * Pricing information for a model
 */
export interface ModelPricing {
  input: number;
  output: number;
  cache_read_input: number;
}

/**
 * Raw leaderboard entry returned by the Nexus /mis/v1/leaderboard endpoint.
 * Metric values range from 0.0 to 5.0 or null when the metric is not applicable.
 * Pricing is returned as a nested object which the service flattens into
 * pricePerMillion* fields on LeaderboardEntry.
 */
export interface NexusLeaderboardEntry {
  modelId: string;
  intelligence: number | null;
  agentic: number | null;
  coding: number | null;
  speed: number | null;
  pricing?: ModelPricing | null;
}

/**
 * Leaderboard entry used across the Model Garden pages. Mapped from
 * NexusLeaderboardEntry, with pricing flattened into pricePerMillion* fields.
 * Metric values range from 0.0 to 5.0 or null when the metric is not applicable.
 */
export interface LeaderboardEntry {
  modelId: string;
  intelligence: number | null;
  agentic: number | null;
  coding: number | null;
  speed: number | null;
  pricePerMillionInputTokens: number | null;
  pricePerMillionOutputTokens: number | null;
}

/**
 * Describes a single leaderboard metric column with display name,
 * descriptive tooltip text, and the key used for sorting.
 */
export interface LeaderboardMetricColumn {
  key: keyof Omit<LeaderboardEntry, 'modelId'>;
  name: string;
  additionalInfo: string;
  methodology?: string;
  includes?: string;
  lowerIsBetter?: boolean;
}

/**
 * Combined view of a leaderboard entry enriched with model details
 * so the table can display name, release date and icon alongside metrics.
 */
export interface LeaderboardRow {
  modelId: string;
  modelName: string;
  releaseDate: string;
  iconId: string;
  pricePerMillionInputTokens: number | null;
  pricePerMillionOutputTokens: number | null;
  intelligence: number | null;
  agentic: number | null;
  coding: number | null;
  speed: number | null;
}
